import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import {
  getLanguageAssociation,
  deleteLanguageAssociation,
  getLanguageDetails,
  updateLanguageAssociation,
} from '../../../store/states/LanguageSlice';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';
import {
  EActionType,
  EDataTableType,
  LanguageAssociationFormFields,
} from '../../../data/Constants';
import { GenericSimpleForm } from '../../Layouts/GenericSimpleForm';
import { TFunction } from 'i18next';

import type {
  IAPIResponse,
  ILanguage,
  ILanguagePropsData,
  ILanguageFormData,
} from '../../../../types';

const handleUpdate = (
  res: IAPIResponse,
  props: {
    data: {
      page: number;
      recordsPerPage: number;
      searchValue: string;
    };
  },
  t: TFunction,
  dispatch: AppDispatch
) => {
  if (res?.data) {
    dispatch(
      getLanguageAssociation({
        page: props.data.page,
        limit: props.data.recordsPerPage,
        searchCode: props.data.searchValue,
      })
    );
    dispatch(
      setAlertMessage({ show: true, message: t('messages.success.update'), type: 'success' })
    );
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : t('messages.fail.update');
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
};

export const LanguageAssociationManipulationForm: React.FC<ILanguagePropsData> = (props) => {
  const { t } = useTranslation();
  const [languagesAvailable, setLanguagesAvailable] = useState<ILanguage[]>([]);
  const [initialFormData, setInitialFormData] = useState<ILanguageFormData>({
    productCode: '',
    languages: [],
  });

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(getLanguageDetails({ request: [] })).then((response: any) => {
      //To fetch all the available languages
      setLanguagesAvailable(response.payload.data);
      const activeLanguages = response.payload.data.filter((obj: ILanguage) => obj.isActive);
      LanguageAssociationFormFields.filter((app) => app.field == 'languages')?.map(
        (app) => (app.values = activeLanguages)
      );
    });
    setInitialFormData({
      productCode: props.data.productCode,
      languages: props.data.languages.map((p) => p.code).sort((a, b) => a.localeCompare(b)),
    });
  }, []);

  useEffect(() => {
    const activeLanguages = languagesAvailable.filter((obj: ILanguage) => obj.isActive);
    LanguageAssociationFormFields.filter((app) => app.field == 'languages')?.map(
      (app) => (app.values = activeLanguages)
    );
  }, [languagesAvailable]);

  const handleSubmit = (data: ILanguageFormData) => {
    const addLang: string[] = [];
    const delLang: string[] = [];
    //find which is present in initial but not in current those are deleted
    //find which is present in current but not in inital those are added

    initialFormData.languages.forEach((element) => {
      if (!data.languages.includes(element)) {
        delLang.push(element);
      }
    });

    data.languages.forEach((element) => {
      if (!initialFormData.languages.includes(element)) {
        addLang.push(element);
      }
    });

    if (addLang.length > 0) {
      dispatch(
        updateLanguageAssociation({
          request: { ...data, languages: addLang, packageType: props.data.packageType },
        })
      ).then((res: any) => {
        handleUpdate(res.payload, props, t, dispatch);
      });
    }

    if (delLang.length > 0) {
      dispatch(
        deleteLanguageAssociation({
          request: { ...data, languages: delLang, packageType: props.data.packageType },
        })
      ).then((res: any) => {
        handleUpdate(res.payload, props, t, dispatch);
      });
    }
  };

  return (
    <Box className="manipulationBox">
      <Box className="manipulationForm">
        <GenericSimpleForm
          formFields={LanguageAssociationFormFields}
          initialData={initialFormData}
          onSubmit={handleSubmit}
          manipulationType={EActionType.Edit}
          type={EDataTableType.LanguageAssociation}
          uniqueKey="productCode"
        />
      </Box>
    </Box>
  );
};
